
import { Component, Vue } from "vue-property-decorator";

interface Pregunta {
  id: number;
  pregunta: string;
  respuesta: string;
}

interface Tema {
  tema: string;
  preguntas: Array<Pregunta>;
}

// Estructura de datos para los resultados de la búsqueda
interface SearchResult {
  label: string; // Texto de la pregunta
  value: number; // Índice del tema padre (temaIndex)
  id: number; // ID de la pregunta (para resaltar)
}

@Component({
  components: {},
})
export default class PreguntasFrecuentesComponent extends Vue {
  // Estado para el buscador
  private searchOptions: SearchResult[] = []; // Opciones filtradas para el QSelect
  // CORRECCIÓN: Al usar emit-value y option-value="id", el v-model recibe el ID, que es un número.
  private searchModel: number | null = null; // Pregunta seleccionada en el QSelect
  private highlightedQuestionId: number | null = null; // ID de la pregunta a resaltar

  // Estado para rastrear el índice del tema seleccionado.
  private selectedTemaIndex: number = 0;

  // Método para actualizar el índice del tema seleccionado al hacer clic
  private selectTema(index: number): void {
    this.selectedTemaIndex = index;
    // Limpiar el resaltado cuando se navega por tema
    this.highlightedQuestionId = null;
  }

  // Propiedad computada que aplana todas las preguntas para la búsqueda
  private get allQuestionsForSearch(): SearchResult[] {
    let results: SearchResult[] = [];
    this.dataPreguntas.forEach((tema, temaIndex) => {
      tema.preguntas.forEach((pregunta) => {
        results.push({
          label: pregunta.pregunta,
          value: temaIndex, // Guarda el índice del tema padre
          id: pregunta.id,
        });
      });
    });
    return results;
  }

  // Método para filtrar las preguntas en el QSelect (se dispara al escribir)
  private filterQuestions(val: string, update: Function): void {
    // Mostrar todas las preguntas al abrir o al borrar texto
    if (!val) {
      update(() => {
        this.searchOptions = [...this.allQuestionsForSearch];
      });
      return;
    }

    // Filtrado normal
    update(() => {
      const needle = val.toLowerCase();
      this.searchOptions = this.allQuestionsForSearch.filter((option) =>
        option.label.toLowerCase().includes(needle)
      );
    });
  }

  // Método ejecutado cuando se selecciona una pregunta del desplegable
  // 'id' es el valor numérico (option-value="id") emitido por el q-select
  private selectQuestionFromSearch(optionOrId: any): void {
    if (!optionOrId) return;

    let selectedQuestion =
      typeof optionOrId === "number"
        ? this.allQuestionsForSearch.find((q) => q.id === optionOrId)
        : optionOrId;

    if (!selectedQuestion) return;

    this.selectedTemaIndex = selectedQuestion.value;
    this.highlightedQuestionId = selectedQuestion.id;

    // 👇 LIMPIAR EL INPUT ANTES DEL SCROLL
    this.searchModel = "";

    this.$nextTick(() => {
      this.$nextTick(() => {
        const cmp = Array.isArray(this.$refs[`question-${selectedQuestion.id}`])
          ? this.$refs[`question-${selectedQuestion.id}`][0]
          : this.$refs[`question-${selectedQuestion.id}`];

        const el = cmp?.$el || cmp;

        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }

        setTimeout(() => (this.highlightedQuestionId = null), 3000);
      });
    });
  }

  // ... (dataPreguntas se mantiene igual, solo se aseguraron IDs únicos en la versión anterior)
  private dataPreguntas: Tema[] = [
    {
      tema: "Accesibilidad y Personas con Movilidad Reducida",
      preguntas: [
        {
          id: 1,
          pregunta: "¿Para quién son los asientos preferenciales?",
          respuesta:
            "<p>Todos los buses del Sistema de Transporte Público de la capital deben tener en la ventana, específicamente al costado de los asientos destinados para uso preferencial, una pieza adhesiva con el símbolo internacional de una silla de ruedas (SIA de color blanco en un recuadro azul) el que indica que estos asientos están reservados para personas con discapacidad que presenten movilidad reducida. Usted puede tener la total libertad de exigir al usuario que esté haciendo uso de este lugar preferencial que le ceda el asiento. También puede dirigirse al conductor para solicitarle que haga cumplir lo que indica el adhesivo mencionado.</p>",
        },
        {
          id: 2,
          pregunta:
            "¿Se puede transportar un coche de niños en los buses del sistema?",
          respuesta:
            "<p>El Artículo N° 91 de la Ley de Tránsito, en el punto N° 4, establece que los conductores de buses de Transporte Público no deben permitir el ingreso de “bultos o paquetes que molesten a los pasajeros o que impidan la circulación por el pasillo del vehículo. Exceptuándose de esta prohibición, los perros de asistencia que acompañen a pasajeros con discapacidad”.<p>",
        },
      ],
    },
    {
      tema: "Infraestructura, Paraderos y Zonas pagas",
      preguntas: [
        {
          id: 3,
          pregunta: "¿Qué son las Vías priorizadas?",
          respuesta:
            "<p>Son pistas que se destinan para el exclusivo uso de buses de Transporte Público, con el objeto de disminuir sustancialmente los tiempos de viaje de los usuarios. Existen dos tipos de vías priorizadas: Vías Exclusivas, que son calles en las que en determinados horarios circulan exclusivamente buses de Transporte Público y taxis en cualquier modalidad en todas sus pistas y las Pista Solo Bus que están destinadas únicamente para buses de Transporte Público durante las 24 horas, todos los días del año. Los taxis podrán circular siempre y cuando tengan pasajeros en su interior. Están ubicadas en el lado derecho de la vía.</p> <p>Actualmente, la ciudad de Santiago cuenta con varias Vías Exclusivas y Pistas Solo Bus.</p> <p>Ver mapa con detalles <a href='http://vias.fiscalizacion.cl/gui/img/Mapa_Vias_Exclusivas_con_Camaras_2014-01.jpg' target='blank'>Aquí</a></p>",
        },
        {
          id: 4,
          pregunta: "¿Cómo puedo conocer una parada?",
          respuesta:
            "<p>Existen 3 tipos de paradas: <br>Parada Simple: se compone por un letrero vertical que indica los recorridos de buses que tienen detención en ese punto como parada oficial del sistema. <br>Parada Refugio: Junto a una Parada Simple, se encuentra un refugio simple <br>Estaciones de Trasbordo: Son un conjunto de paradas grandes que se ubican en lugares de alta afluencia de usuarios del Transporte Público, siendo puntos de conexión entre modos de transportes, tales como buses y Metro. Muchas de las Estaciones de Transbordo se ubican a las afueras de estaciones de Metro.</p> <p>Para más detalle visite la sección de Infraestructura en <a href='https://www.dtpm.cl/' target='blank'>www.dtpm.cl</a></p>",
        },
      ],
    },
    {
      tema: "Recorridos y Buses",
      preguntas: [
        {
          id: 5,
          pregunta:
            "¿Dónde puedo conocer el trazado o ruta de los diferentes recorridos de buses?",
          respuesta:
            "<p>Para conocer el trazados de los buses de Transantiago puede consultar en Mapas y Recorridos, específicamente en la sección Detalle de los Recorridos. Ahí encontrará, además de los trazados de cada recorrido, sus paradas y horarios.</p>",
        },
        {
          id: 6,
          pregunta: "¿Cuáles son las empresas de buses de Transantiago?",
          respuesta:
            "<p>Las empresas concesionarias de los recorridos de buses de Transantiago son las siguientes: <br>Buses Metropolitana: Representado por el color Turquesa y prestando los servicios 500 y J <br>Alsacia: Representado por el color Celeste y prestando los servicios 100, 408, 408e, 410 y 410e <br>Express: Representado por el color Naranjo y prestando los servicios 400 y D <br>SuBus: Representado por el color Azul y prestando los servicios 200 y G <br>Vule: Representado por el color Verde y prestando los servicios 300, E, H e I <br>RedBus: Representado por el color Rojo y prestando los servicios B y C <br>STP: Representado por el color Amarillo y prestando los servicios F.</p> <p>Para conocer más detalles ingrese aquí <a href='http://www.transantiago.cl/acerca-de-transantiago/detalle-de-empresas' target='blank'>Link</a></p>",
        },
        {
          id: 7,
          pregunta:
            "¿Qué significan las letras e/c/v/n que se encuentran al lado del nombre del recorrido de buses?",
          respuesta:
            "<p>Con la letra e se identifican los recorridos que son expresos, es decir, aquellos que para llegar más rápido a su destino tienen menos paradas durante el trayecto.</p> <p>Con la letra c se identifican los recorridos cortos, es decir, aquellos que presentan un trazado más corto entre sus puntos de inicio y término.</p> <p>La letra v significa variante. De esta manera, se identifican los recorridos que presentan un trazado diferente al recorrido de origen.</p> <p>Con la letra n se identifican los recorridos nocturnos, es decir aquellos que operan con un determinado trazado sólo en las noches.</p>",
        },
        {
          id: 8,
          pregunta: "¿Qué son los recorridos con itinerarios?",
          respuesta:
            "<p>Son algunos recorridos de buses de Transantiago presentan horarios establecidos de pasada por algunas de sus paradas. Así, podrá programar su hora de llegada al paradero y esperar menos por su bus. A la fecha hay diez servicios de Transantiago que ya están operando con itinerarios. Para conocer los horarios pulse <a href='http://www.transantiago.cl/noticias/recorridos-con-itinerarios' target='blank'>aquí</a></p>",
        },
      ],
    },
    {
      tema: "Información y Atención de Usuarios",
      preguntas: [
        {
          id: 9,
          pregunta: "¿Se puede transportar una bicicleta en el bus?",
          respuesta:
            "<p>Podrás transportar tu bicicleta en los buses de Red Movilidad únicamente los días domingo y feriados entre 06:00 am y la medianoche.</p> <p>Debes tener en cuenta que solo podrás subir tu bicicleta a los buses si el espacio reservado no está ocupado por personas con movilidad reducida o quienes trasladen a niños y niñas en coche.</p> <p>Recuerda que siempre deberás subir tu bicicleta a los buses por la segunda puerta y luego pagar tu pasaje con la tarjeta bip! o código QR.</p> <p>Durante el viaje, mantén tu bici junto a ti y de pie, así no afectarás el desplazamiento de las otras personas. Y, para bajar, hazlo siempre también por la segunda puerta del bus.</p>",
        },
        {
          id: 10,
          pregunta: "¿Dónde se pueden hacer reclamos, denuncias o consultas?",
          respuesta:
            "<p>Usted puede hacer reclamos, denuncias o consultas a través de nuestros canales oficiales de atención: Call Center 800 73 00 73 (gratuito desde teléfonos fijos) o 600 730 00 73 (desde celulares y/o regiones, costo de una llamada local), página web y RRSS de Transantiago. Cuando realice una denuncia o reclamo, ya sea a través del Call Center o por medio de los otros canales de atención, recuerde entregar todos los datos solicitados. Sólo así se podrá dar curso a su requerimiento. De acuerdo a la naturaleza de la denuncia o el reclamo, estos datos podrían ser, entre otros, la placa patente del bus, número de recorrido, hora, día y lugar, donde ocurrió el incidente.</p>",
        },
      ],
    },
    {
      tema: "Nuevos Recorridos y Modificaciones",
      preguntas: [
        {
          id: 11,
          pregunta: "¿Cómo operan los servicios nocturnos?",
          respuesta:
            "<p>El Sistema de Transporte Público de Santiago ofrece recorridos de buses que operan las 24 horas y otros que prestan servicio exclusivamente en horario nocturno, entre la 01:00 y las 05:30 hrs. La cobertura nocturna que ofrece el Sistema de Transporte Público de Santiago permite la conexión entre comunas alejadas y el centro de la ciudad. Además, los recorridos nocturnos prestan servicio en los principales puntos donde se concentra la actividad nocturna de la ciudad, tales como Av. Providencia, Plaza Italia, Alameda, Vicuña Mackenna, Irarrázaval y Bellavista, entre otros</p>",
        },
      ],
    },
    {
      tema: "Tarjeta bip! y Tarifas",
      preguntas: [
        {
          id: 12,
          pregunta: "¿Cuál es el valor de la tarifa?",
          respuesta:
            "<p>La Tarjeta bip! es el medio de pago oficial para Buses de Transantiago, Metro y ahora el sistema MetroTren Nos. Al pagar su viaje con la Tarjeta bip!, podrá realizar hasta dos transbordos sin costo adicional. Esto durante 2 horas (120 minutos), desde el inicio de la primera etapa del viaje o primer bip! (en la misma dirección y sin repetición de recorridos de buses). Puede utilizar uno de los transbordos en Metro y otro en el sistema MetroTren Nos. Conozca detalles de la tarifaLa Tarjeta bip! es el medio de pago oficial para Buses de Transantiago, Metro y ahora el sistema MetroTren Nos. Al pagar su viaje con la Tarjeta bip!, podrá realizar hasta dos transbordos sin costo adicional. Esto durante 2 horas (120 minutos), desde el inicio de la primera etapa del viaje o primer bip! (en la misma dirección y sin repetición de recorridos de buses). Puede utilizar uno de los transbordos en Metro y otro en el sistema MetroTren Nos. Conozca detalles de la tarifa <a href='http://www.transantiago.cl/tarifas-y-pagos/conoce-las-tarifas' target='blank'>aquí</a></p>",
        },
        {
          id: 13,
          pregunta: "¿Qué es el viaje de emergencia?",
          respuesta:
            "<p>Todas las tarjetas bip! tienen disponible un Viaje de Emergencia que le permite realizar un único viaje con transbordo en buses de Transantiago, aunque no cuente con saldo suficiente en tu tarjeta bip! El monto utilizado se le descontará en la próxima carga. Puede hacer uso de este beneficio las 24 horas, los 365 días del año.</p>",
        },
        {
          id: 14,
          pregunta: "¿Qué es la tarjeta bip!?",
          respuesta:
            "<p>Es el medio de pago del sistema de transportes del Transantiago, que incluye buses y Metro. La tarjeta bip! te permite utilizar el mismo medio de pago, a través de una tarifa integrada, tanto en buses como en Metro, MetroTren Nos o la combinación entre éstos.</p>",
        },
        {
          id: 15,
          pregunta: "¿Cómo se realiza la carga bip! en línea?",
          respuesta:
            "<p>La carga de tarjeta bip! en línea se efectúa con tarjeta de débito o tarjeta de crédito. A través de este sistema, podrá ahorrar tiempo y sus transacciones se realizarán de forma segura mediante WebPay. Para ver reflejado el nuevo saldo en tu tarjeta, sólo tiene que pasar por un punto Infobip o tótem de Metro. Si desea cargar ingrese <a href='https://carga.tarjetabip.cl/' target='blank'>aquí</a></p>",
        },
      ],
    },
  ];
}
